"use client";

import Link from "next/link";
import { useState } from "react";
import { Container } from "@/components/ui/container";
import { Section } from "@/components/ui/section";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { withCommunityReturnTo } from "@/lib/community/navigation";
import { CommunityImage } from "../CommunityImage";
import { ShopAvatar } from "../ShopAvatar";
import { AddToCartButton } from "../cart/add-to-cart-button";
import { QuantityStepper } from "../cart/quantity-stepper";
import type { PublicCommunityProductDetailResponse } from "@/lib/community/types";

interface ProductDetailPageProps {
  slug: string;
  returnTo?: string;
  product: PublicCommunityProductDetailResponse["product"];
  variants: PublicCommunityProductDetailResponse["variants"];
  shop: PublicCommunityProductDetailResponse["shop"];
}

const focusRing =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600 focus-visible:ring-offset-2";

function formatPrice(amount: string, currencyCode: string | null): string {
  const value = Number(amount);
  if (!currencyCode || !Number.isFinite(value)) return amount;

  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currencyCode,
    }).format(value);
  } catch {
    return amount;
  }
}

export function ProductDetailPage({
  slug,
  returnTo,
  product,
  variants,
  shop,
}: ProductDetailPageProps) {
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [selectedVariantId, setSelectedVariantId] = useState<string | null>(
    () =>
      (variants.find((variant) => variant.available) ?? variants[0])?.id ??
      null,
  );
  const [quantity, setQuantity] = useState(1);

  const images = product.images;
  const activeImage = images[selectedImageIndex] ?? images[0] ?? null;
  const selectedVariant =
    variants.find((variant) => variant.id === selectedVariantId) ??
    variants[0] ??
    null;
  const productTitle = product.title ?? "Untitled product";
  const shopName = shop.name ?? slug;
  const categoryName =
    product.category_name === "Uncategorized" ? null : product.category_name;

  return (
    <Section background="white" size="sm">
      <Container size="xl">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-2">
          <div>
            <div className="relative aspect-square overflow-hidden rounded-lg border border-line bg-raised">
              <CommunityImage
                src={activeImage}
                alt={productTitle}
                fill
                sizes="(max-width: 1024px) 100vw, 50vw"
                priority
                containerClassName="absolute inset-0"
              />
            </div>
            {images.length > 1 && (
              <div className="mt-4 flex gap-3 overflow-x-auto pb-1">
                {images.map((image, index) => {
                  const isActive = index === selectedImageIndex;
                  return (
                    <button
                      key={`${index}-${image}`}
                      type="button"
                      onClick={() => setSelectedImageIndex(index)}
                      aria-label={`View image ${index + 1} of ${images.length}`}
                      aria-pressed={isActive}
                      className={cn(
                        "relative h-20 w-20 shrink-0 overflow-hidden rounded-md border transition-colors",
                        focusRing,
                        isActive
                          ? "border-primary-600 ring-1 ring-primary-600"
                          : "border-line hover:border-line-strong",
                      )}
                    >
                      <CommunityImage
                        src={image}
                        alt=""
                        fill
                        sizes="80px"
                        containerClassName="absolute inset-0"
                      />
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="flex flex-col">
            <Link
              href={withCommunityReturnTo(`/community/${slug}`, returnTo)}
              className={cn("group mb-6 block rounded-lg", focusRing)}
            >
              <Card className="rounded-lg border-retro-line/20 shadow-none transition-colors group-hover:border-retro-line/60 group-focus-visible:border-retro-line/60">
                <div className="flex items-center gap-2.5 p-3">
                  <ShopAvatar shop={shop} size="sm" />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs text-muted">Sold by</p>
                    <p className="truncate text-sm font-medium text-ink">
                      {shopName}
                    </p>
                  </div>
                  <span className="shrink-0 border-2 border-retro-line bg-retro-paper px-2.5 py-1.5 font-mono text-[10px] font-black uppercase tracking-[0.08em] text-brand-green shadow-[1px_1px_0_#173c17] transition-[background-color,color,box-shadow] duration-150 group-hover:bg-primary-900 group-hover:text-white group-hover:shadow-none group-focus-visible:bg-primary-900 group-focus-visible:text-white group-focus-visible:shadow-none">
                    View store
                  </span>
                </div>
              </Card>
            </Link>

            <h1 className="text-h2 text-ink">{productTitle}</h1>

            {selectedVariant && (
              <div className="mt-4 flex items-baseline gap-3">
                <p className="text-xl font-semibold text-brand-green">
                  {formatPrice(selectedVariant.price, shop.currency_code)}
                </p>
                {!selectedVariant.available && (
                  <p className="text-sm font-medium text-muted">
                    Out of stock
                  </p>
                )}
              </div>
            )}

            {variants.length > 1 && (
              <div
                role="group"
                aria-label="Product options"
                className="mt-6 flex max-h-64 flex-wrap gap-2 overflow-y-auto overscroll-contain rounded-md border border-retro-line/25 bg-retro-paper/50 p-3"
              >
                {variants.map((variant) => {
                  const label =
                    variant.selected_options.length > 0
                      ? variant.selected_options
                          .map((option) => option.value)
                          .join(" / ")
                      : (variant.title ?? "Default");
                  const isSelected = variant.id === selectedVariant?.id;
                  return (
                    <button
                      key={variant.id}
                      type="button"
                      onClick={() => setSelectedVariantId(variant.id)}
                      aria-pressed={isSelected}
                      aria-label={
                        variant.available ? label : `${label} (out of stock)`
                      }
                      className={cn(
                        "inline-flex min-h-[44px] items-center rounded-full border px-5 text-sm font-medium transition-colors",
                        focusRing,
                        !variant.available &&
                          "border-line bg-page text-muted line-through",
                        variant.available &&
                          isSelected &&
                          "border-line-strong bg-ink text-page",
                        variant.available &&
                          !isSelected &&
                          "border-line bg-panel text-muted hover:border-line-strong hover:text-ink",
                      )}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            )}

            {selectedVariant && (
              <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
                <QuantityStepper
                  value={quantity}
                  onChange={setQuantity}
                  disabled={!selectedVariant.available}
                />
                <AddToCartButton
                  shopSlug={slug}
                  variantId={selectedVariant.id}
                  quantity={quantity}
                  disabled={!selectedVariant.available}
                  label={
                    selectedVariant.available ? "Add to cart" : "Out of stock"
                  }
                  className="flex-1"
                />
              </div>
            )}

            {(categoryName || product.description_html) && (
              <section
                aria-labelledby="product-description-heading"
                className="mt-10 border-t border-line pt-8"
              >
                <h2
                  id="product-description-heading"
                  className="text-h3 text-ink"
                >
                  About this product
                </h2>
                {categoryName && (
                  <p className="mt-4 inline-flex rounded-md border border-retro-line/25 bg-retro-paper/50 px-2.5 py-1 font-mono text-[11px] font-bold uppercase tracking-[0.08em] text-brand-green">
                    {categoryName}
                  </p>
                )}
                {product.description_html && (
                  <div
                    className="mt-4 leading-relaxed text-muted [&_a]:text-brand-green [&_a]:underline [&_a:hover]:text-brand-green [&_h1]:mb-3 [&_h1]:font-semibold [&_h1]:text-ink [&_h2]:mb-3 [&_h2]:font-semibold [&_h2]:text-ink [&_h3]:mb-3 [&_h3]:font-semibold [&_h3]:text-ink [&_img]:rounded-lg [&_ol]:mb-4 [&_ol]:list-decimal [&_ol]:pl-6 [&_p]:mb-4 [&_ul]:mb-4 [&_ul]:list-disc [&_ul]:pl-6"
                    dangerouslySetInnerHTML={{
                      __html: product.description_html,
                    }}
                  />
                )}
              </section>
            )}
          </div>
        </div>
      </Container>
    </Section>
  );
}
